import { useEffect } from "react";

/**
 * Lazily loads platform widget scripts (X / Instagram / TikTok) and reparses
 * the DOM so any freshly-mounted `<blockquote class="twitter-tweet">`,
 * `.instagram-media`, or `.tiktok-embed` gets upgraded to the full card.
 * Call once per article render. Safe to call multiple times — scripts are
 * cached and each platform ships an idempotent reload API.
 *
 * Note: on strict CSP setups you may need to allow platform.twitter.com,
 * instagram.com, and tiktok.com — most fan sites already do.
 */
export function useSocialEmbeds(depKey) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const needsTwitter = !!document.querySelector(".twitter-tweet");
    const needsInsta = !!document.querySelector(".instagram-media");
    const needsTikTok = !!document.querySelector(".tiktok-embed");

    const load = (src, id) =>
      new Promise((res) => {
        if (document.getElementById(id)) return res(true);
        const s = document.createElement("script");
        s.src = src;
        s.id = id;
        s.async = true;
        s.charset = "utf-8";
        s.onload = () => res(true);
        s.onerror = () => res(false);
        document.head.appendChild(s);
      });

    (async () => {
      if (needsTwitter) {
        await load("https://platform.twitter.com/widgets.js", "twitter-widgets");
        // Ask the widget lib to (re-)scan the DOM.
        window.twttr?.widgets?.load?.();
      }
      if (needsInsta) {
        await load("https://www.instagram.com/embed.js", "instagram-embed");
        window.instgrm?.Embeds?.process?.();
      }
      if (needsTikTok) {
        await load("https://www.tiktok.com/embed.js", "tiktok-embed");
      }
    })();
    // depKey changes when we swap articles → forces re-scan
  }, [depKey]);
}
